import type { Id } from '../types';
import type { ProductPos } from './model';
import { compareIds, maxPosOf, medianGapOf, withoutGuessed, type Positions } from './positions';

/** Odhaczenia szybsze niż ten odstęp tworzą serię. */
export const BURST_GAP_MS = 4000;
/** Seria co najmniej tylu odhaczeń to jedna grupa remisowa. */
const BURST_MIN = 3;
/** Krótsze wizyty nic nie mówią o trasie. */
const MIN_GROUPS = 3;
const MAX_WEIGHT = 5;
/** Ręczna pozycja „przeczy” wizycie, gdy miejsca różnią się o więcej niż tyle. */
const MANUAL_RANK_TOLERANCE = 2;
/** Tyle kolejnych zaprzeczeń zwalnia ręczną pozycję. */
const MANUAL_RELEASE_AFTER = 3;
/** Tyle kolejnych wizyt z produktem nie na swoim miejscu = półka przeniesiona, przyjmujemy nowe miejsce w całości. */
const RELOCATE_AFTER = 2;

export interface RouteCheck {
  productId: Id;
  wallMs: number;
}

/**
 * Dzieli trasę (unikalne produkty w kolejności odhaczania) na kroki.
 * Zwraca null, gdy po odrzuceniu końcowej serii (kasa) zostaje mniej niż 3 kroki.
 */
export function groupChecks(route: readonly RouteCheck[]): Id[][] | null {
  const groups: Id[][] = [];
  let lastIsBurst = false;
  for (let i = 0; i < route.length; ) {
    let j = i + 1;
    while (j < route.length && Math.abs(route[j]!.wallMs - route[j - 1]!.wallMs) < BURST_GAP_MS) j++;
    const run = route.slice(i, j).map((c) => c.productId);
    lastIsBurst = run.length >= BURST_MIN;
    if (lastIsBurst) groups.push(run);
    else for (const id of run) groups.push([id]);
    i = j;
  }
  if (lastIsBurst) groups.pop();
  return groups.length >= MIN_GROUPS ? groups : null;
}

interface KnownCheck {
  id: Id;
  g: number;
  p: ProductPos;
}

const weightOf = (p: ProductPos) => Math.min(Math.max(p.n, 1), MAX_WEIGHT);

/** Ręcznie ustawione produkty, których miejsce w tej wizycie odbiega o więcej niż 2 od wyuczonego. */
function manualContradictions(known: readonly KnownCheck[]): Set<Id> {
  const out = new Set<Id>();
  if (!known.some((k) => k.p.manual)) return out;
  const learned = [...known].sort((a, b) => a.p.pos - b.p.pos || compareIds(a.id, b.id));
  const learnedRank = new Map(learned.map((k, i) => [k.id, i]));
  let visitRank = 0;
  known.forEach((k, i) => {
    if (i > 0 && known[i - 1]!.g !== k.g) visitRank = i;
    if (k.p.manual && Math.abs(learnedRank.get(k.id)! - visitRank) > MANUAL_RANK_TOLERANCE) out.add(k.id);
  });
  return out;
}

/**
 * Odporna regresja izotoniczna: najcięższy podzbiór znanych produktów, których pozycje są
 * niemalejące w kolejności wizyty (ważony najdłuższy podciąg niemalejący, wagi min(n, 5)).
 * Te produkty to kotwice; pozostałe znane są „nie na swoim miejscu” i dostają cel jak nowe.
 * W przeciwieństwie do PAVA (L2) jeden przestawiony produkt nie ściąga ku sobie sąsiadów.
 */
function anchorsOf(known: readonly KnownCheck[]): Set<Id> {
  const items = [...known].sort((a, b) => a.g - b.g || a.p.pos - b.p.pos || compareIds(a.id, b.id));
  const best: number[] = [];
  const prev: number[] = [];
  let end = -1;
  items.forEach((it, i) => {
    const w = weightOf(it.p);
    best[i] = w;
    prev[i] = -1;
    for (let j = 0; j < i; j++) {
      if (items[j]!.p.pos <= it.p.pos && best[j]! + w > best[i]!) {
        best[i] = best[j]! + w;
        prev[i] = j;
      }
    }
    if (end < 0 || best[i]! > best[end]!) end = i;
  });
  const out = new Set<Id>();
  for (let i = end; i >= 0; i = prev[i]!) out.add(items[i]!.id);
  return out;
}

const alphaOf = (p: ProductPos) => (p.n === 0 ? 1 : p.seeded && p.n === 1 ? 0.8 : 0.4);

/**
 * Uczy model jednej trasy (jedno urządzenie, jedna wizyta) podzielonej na kroki.
 * Produkt ze zgadniętym miejscem jest tu nieznany: nie jest kotwicą i nie wyznacza odstępów ani końca trasy.
 */
export function learnSequence(byProduct: Positions, groups: readonly (readonly Id[])[]): void {
  const known: KnownCheck[] = [];
  groups.forEach((group, g) => {
    for (const id of group) {
      const p = byProduct.get(id);
      if (p && !p.guessed) known.push({ id, g, p });
    }
  });
  const contradicted = manualContradictions(known);
  const anchors = anchorsOf(known.filter((k) => !contradicted.has(k.id)));

  // Cel kroku z kotwicami: ich średnia pozycja ważona pewnością. Kotwice są uporządkowane, więc cele rosną.
  const obsG: number[] = [];
  const obsY: number[] = [];
  const obsW: number[] = [];
  for (const k of known) {
    if (!anchors.has(k.id)) continue;
    const w = weightOf(k.p);
    const last = obsG.length - 1;
    if (last >= 0 && obsG[last] === k.g) {
      obsY[last] = (obsY[last]! * obsW[last]! + w * k.p.pos) / (obsW[last]! + w);
      obsW[last]! += w;
    } else {
      obsG.push(k.g);
      obsY.push(k.p.pos);
      obsW.push(w);
    }
  }

  // Kroki bez kotwic: interpolacja między sąsiednimi krokami z kotwicami, za końcami — co medianę odstępu.
  let settledCache: ProductPos[] | undefined;
  const settled = () => (settledCache ??= withoutGuessed(byProduct.values()));
  let gapCache: number | undefined;
  const gap = () => (gapCache ??= medianGapOf(settled()));
  const y = new Array<number>(groups.length);
  if (obsG.length === 0) {
    const base = settled().length === 0 ? 0 : maxPosOf(settled()) + gap();
    for (let g = 0; g < groups.length; g++) y[g] = base + g;
  } else {
    const first = obsG[0]!;
    for (let g = 0; g < first; g++) y[g] = obsY[0]! - (first - g) * gap();
    obsG.forEach((g0, i) => {
      const y0 = obsY[i]!;
      y[g0] = y0;
      const g1 = obsG[i + 1];
      if (g1 === undefined) {
        for (let g = g0 + 1; g < groups.length; g++) y[g] = y0 + (g - g0) * gap();
      } else {
        const y1 = obsY[i + 1]!;
        for (let g = g0 + 1; g < g1; g++) y[g] = y0 + ((y1 - y0) * (g - g0)) / (g1 - g0);
      }
    });
  }

  groups.forEach((group, g) => {
    const target = y[g]!;
    for (const id of group) {
      const p = byProduct.get(id);
      // Nowy albo zgadnięty: pierwsza prawdziwa obserwacja ustala miejsce w całości (bez pola guessed).
      if (!p || p.guessed) {
        byProduct.set(id, { pos: target, n: 1, manual: false, contra: 0, seeded: false });
        continue;
      }
      let alpha = alphaOf(p);
      if (p.manual) {
        p.contra = contradicted.has(id) ? p.contra + 1 : 0;
        if (p.contra < MANUAL_RELEASE_AFTER) continue;
        p.manual = false;
        p.contra = 0;
      } else if (anchors.has(id)) {
        p.contra = 0;
      } else if (++p.contra >= RELOCATE_AFTER) {
        alpha = 1;
        p.contra = 0;
      }
      p.pos += alpha * (target - p.pos);
      p.n += 1;
      p.seeded = false;
    }
  });
}
